import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button, Modal, Form, Dropdown } from 'react-bootstrap';
import { AutoSizer, Column, Table as VirtualizedTable } from 'react-virtualized';
import { FaLock, FaCheck, FaHourglassHalf } from 'react-icons/fa';

import LoadingSpinner from '../../../Loading-Component/Loading';
import { formatDateValue } from '../utils/shiftHelpers';

/** 24h time input (HH:mm) with two text fields and auto-advance on 2 digits (libraries on this are shit and kept bugging out) */
const TimeInput = ({ value, onChange, id, className }) => {
    const [displayHH, displayMM] = (value || '00:00').split(':');
    const [editing, setEditing] = React.useState(null);
    const [hourText, setHourText] = React.useState('');
    const [minText, setMinText] = React.useState('');
    const hourRef = React.useRef(null);
    const minRef = React.useRef(null);

    const pad = (v, max) => {
        const n = parseInt(v, 10);
        if (isNaN(n) || v === '') return '00';
        return String(Math.min(Math.max(n, 0), max)).padStart(2, '0');
    };

    const commitHour = (raw) => {
        const clamped = pad(raw, 23);
        onChange(`${clamped}:${displayMM.padStart(2, '0')}`);
    };

    const commitMin = (raw) => {
        const clamped = pad(raw, 59);
        onChange(`${displayHH.padStart(2, '0')}:${clamped}`);
    };

    const handleHourFocus = () => {
        setEditing('hh');
        setHourText('');
    };

    const handleMinFocus = () => {
        setEditing('mm');
        setMinText('');
    };

    const handleHourChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        if (raw.length > 2) return;
        setHourText(raw);
        if (raw.length === 2) {
            commitHour(raw);
            setEditing('mm');
            setMinText('');
            setTimeout(() => minRef.current?.focus(), 0);
        }
    };

    const handleMinChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        if (raw.length > 2) return;
        setMinText(raw);
        if (raw.length === 2) {
            commitMin(raw);
            setEditing(null);
            setTimeout(() => minRef.current?.blur(), 0);
        }
    };

    const handleHourBlur = () => {
        if (editing === 'hh') {
            if (hourText) commitHour(hourText);
            setEditing(null);
        }
    };

    const handleMinBlur = () => {
        if (editing === 'mm') {
            if (minText) commitMin(minText);
            setEditing(null);
        }
    };

    const handleHourKeyDown = (e) => {
        if (e.key === ':' || (e.key === 'Tab' && !e.shiftKey)) {
            if (e.key === ':') e.preventDefault();
            if (hourText) commitHour(hourText);
            setEditing('mm');
            setMinText('');
            setTimeout(() => minRef.current?.focus(), 0);
            if (e.key === 'Tab') e.preventDefault();
        }
    };

    return (
        <div className={`time-input ${className || ''}`} id={id}>
            <input
                ref={hourRef}
                type="text"
                inputMode="numeric"
                maxLength={2}
                value={editing === 'hh' ? hourText : displayHH.padStart(2, '0')}
                onChange={handleHourChange}
                onFocus={handleHourFocus}
                onBlur={handleHourBlur}
                onKeyDown={handleHourKeyDown}
                className="time-input__field"
                placeholder="HH"
            />
            <span className="time-input__sep">:</span>
            <input
                ref={minRef}
                type="text"
                inputMode="numeric"
                maxLength={2}
                value={editing === 'mm' ? minText : displayMM.padStart(2, '0')}
                onChange={handleMinChange}
                onFocus={handleMinFocus}
                onBlur={handleMinBlur}
                className="time-input__field"
                placeholder="MM"
            />
        </div>
    );
};

const RosterDesktopTable = ({
    rosterData,
    roles: rolesFromParent,
    financialWeeks,
    dateFilters,
    selectedFinancialWeek,
    selectedSite,
    createShiftVals,
    futureDateRestrictProp,
    pendingShifts: pendingShiftsFromParent,
    setPendingShifts: setPendingShiftsParent,
    completedPendingIds,
    setRosterData,
    onDuplicate,
    onDuplicateWeek,
    onRemove,
    onRefresh,
    onShiftCreate,
    showToast,
    showSecurityCosting,
    databaseName,
    noRosterDataMsg
}) => {
        const [, setSelectAll] = useState(false);
        const [showDupModal, setShowDupModal] = useState(false);
        const [showRemoveModal, setShowRemoveModal] = useState(false);
        const [loading] = useState(false);
        const [checkedRows, setCheckedRows] = useState({});
        const [duplicateDate, setDuplicateDate] = useState(null);
        const [duplicateFinancialWeek, setDuplicateFinancialWeek] = useState({ description: 'Financial Week' });
        const [weekSelectStatus, setWeekSelectStatus] = useState({});
        const [selectedWeek, setSelectedWeek] = useState(null);
        const [showDupWeekModal, setShowDupWeekModal] = useState(false);
        const handleCloseRemoveModal = () => setShowRemoveModal(false);
        const handleCloseDupModal = () => setShowDupModal(false);

        const handleCloseDupWeekModal = () => setShowDupWeekModal(false);

        const SHIFT_VALUES = useRef({
            date: '',
            startTime: '00:00',
            endTime: '00:00',
            role: 'DS',
            qty: 1,
            notes: ''
        }).current;

        const processedData = useMemo(() => {
            const normaliseDate = (dateStr) => {
                const date = new Date(dateStr);
                return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            };

            const generateWeeksInRange = () => {
                const uniqueWeekEndingsMap = new Map();

                rosterData.forEach(row => {
                    if (row.weekEnding) {
                        const normDate = normaliseDate(row.weekEnding);
                        uniqueWeekEndingsMap.set(normDate, row.weekEnding);
                    }
                });
                
                if (dateFilters.shiftStart && dateFilters.shiftFinish) {
                    const startDate = new Date(dateFilters.shiftStart);
                    const endDate = new Date(dateFilters.shiftFinish);
                    
                    const firstWeekEnding = new Date(startDate);
                    const startDay = firstWeekEnding.getDay();

                    if (startDay !== 0) {
                        firstWeekEnding.setDate(firstWeekEnding.getDate() + (7 - startDay));
                    }

                    let currentWeekEnding = new Date(firstWeekEnding);

                    while (currentWeekEnding <= endDate) {
                        const normDate = normaliseDate(currentWeekEnding);
                        
                        if (!uniqueWeekEndingsMap.has(normDate)) {
                            const cleanDate = new Date(currentWeekEnding.getFullYear(), currentWeekEnding.getMonth(), currentWeekEnding.getDate());
                            uniqueWeekEndingsMap.set(normDate, cleanDate.toISOString());
                        }

                        currentWeekEnding.setDate(currentWeekEnding.getDate() + 7);
                    }
                }

                if (selectedFinancialWeek && selectedFinancialWeek.weekEnding) {
                    const normDate = normaliseDate(selectedFinancialWeek.weekEnding);
                    if (!uniqueWeekEndingsMap.has(normDate)) {
                        uniqueWeekEndingsMap.set(normDate, selectedFinancialWeek.weekEnding);
                    }
                }
                
                return Array.from(uniqueWeekEndingsMap.values());
            };
            
            const allWeekEndings = generateWeeksInRange();


            const weekGroups = {};

            allWeekEndings.forEach(weekEnding => {
                weekGroups[weekEnding] = {
                    rows: [],
                    totalHours: 0,
                    totalShifts: 0,
                    totalCost: 0,
                    futureDateRestrictProp: true
                };
            });
            
            rosterData.forEach(row => {
                if (!row.weekEnding) return;

                const normDate = normaliseDate(row.weekEnding);
                const matchingWeekEnding = allWeekEndings.find(we => normaliseDate(we) === normDate);

                if (matchingWeekEnding) {
                    weekGroups[matchingWeekEnding].rows.push(row);

                    const extractTime = (val) => {
                        if (typeof val === 'string' && val.includes('T')) {
                            const timePart = val.split('T')[1];
                            const [h, m] = timePart.split(':').map(Number);
                            return h * 60 + m;
                        }
                        const d = new Date(val);
                        return d.getHours() * 60 + d.getMinutes();
                    };
                    const startMins = extractTime(row.shiftStart);
                    let endMins = extractTime(row.shiftFinish);
                    if (endMins <= startMins) endMins += 24 * 60;
                    const hours = (endMins - startMins) / 60;
                    const futureDateRestrictProp = row.futureDateRestrictProp;

                    weekGroups[matchingWeekEnding].totalHours += hours;
                    weekGroups[matchingWeekEnding].totalShifts += 1;
                    weekGroups[matchingWeekEnding].totalCost += hours * (row.billRate || 0);
                    weekGroups[matchingWeekEnding].futureDateRestrictProp = futureDateRestrictProp;
                }
            });

            const sortedWeekEndings = Object.keys(weekGroups).sort((a, b) =>
                new Date(a) - new Date(b)
            );

            const flatData = [];
            const subheaderIndices = new Set();
            const headerRowIndices = new Set();
            const addShiftIndices = new Set();
            const originalToFlatMap = {};
            const flatToOriginalMap = {};
            const weekStartDates = {};
            const weekRowIndices = {};
            const dataRowIndexMap = {};
            let dataRowCounter = 0;

            sortedWeekEndings.forEach(weekEnding => {
                const group = weekGroups[weekEnding];

                const endDate = new Date(weekEnding);
                const startDate = new Date(endDate);
                startDate.setDate(endDate.getDate() - 6);

                weekStartDates[weekEnding] = startDate;
                weekRowIndices[weekEnding] = [];

                const formattedStartDate = startDate.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit'
                });

                const formattedEndDate = endDate.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit'
                });

                subheaderIndices.add(flatData.length);
                flatData.push({
                    isSubheader: true,
                    weekEnding: weekEnding,
                    weekStartDate: startDate,
                    weekEndDate: endDate,
                    displayDateRange: `${formattedStartDate} to ${formattedEndDate}`,
                    totalHours: group.totalHours.toFixed(2),
                    totalShifts: group.totalShifts,
                    totalCost: group.totalCost.toFixed(2),
                    futureDateRestrictProp: group.futureDateRestrict
                });

                headerRowIndices.add(flatData.length);
                flatData.push({
                    isHeaderRow: true,
                    weekEnding: weekEnding
                });

                addShiftIndices.add(flatData.length);
                dataRowIndexMap[flatData.length] = dataRowCounter++;
                flatData.push({
                    isAddShiftRow: true,
                    weekStartDate: startDate,
                    weekEnding: weekEnding
                });

                group.rows.forEach(row => {
                    const originalIndex = rosterData.indexOf(row);
                    const flatIndex = flatData.length;

                    dataRowIndexMap[flatIndex] = dataRowCounter++;
                    flatData.push(row);
                    originalToFlatMap[originalIndex] = flatIndex;
                    flatToOriginalMap[flatIndex] = originalIndex;

                    weekRowIndices[weekEnding].push(originalIndex);
                });
            });

            return {
                flatData,
                subheaderIndices,
                headerRowIndices,
                addShiftIndices,
                originalToFlatMap,
                flatToOriginalMap,
                weekStartDates,
                weekRowIndices,
                dataRowIndexMap
            };
        }, [rosterData, dateFilters.shiftStart, dateFilters.shiftFinish, selectedFinancialWeek]);

        const TableOptions = () => {
            const handleRemove = () => {
                setShowRemoveModal(true);
            };

            const handleDuplicate = () => {
                setShowDupModal(true);
            };

            return (
                <div className="table-options">
                    <Button variant="primary" onClick={handleDuplicate}>
                        Duplicate Selected
                    </Button>
                    <Button
                        disabled={isButtonDisabled}
                        variant="primary" onClick={handleRemove}
                    >
                        Remove Selected Shifts
                    </Button>
                </div>
            );
        };

        const [isButtonDisabled, setIsButtonDisabled] = useState(false);

        const handleSelectWeek = (weekEnding) => {
            const indices = processedData.weekRowIndices[weekEnding] || [];
            const allSelected = indices.every(index => !!checkedRows[index]);
            
            setWeekSelectStatus(prev => ({
                ...prev,
                [weekEnding]: !allSelected
            }));
            
            if (allSelected) {
                setCheckedRows(prev => {
                    const newCheckedRows = { ...prev };
                    indices.forEach(index => {
                        delete newCheckedRows[index];
                    });
                    return newCheckedRows;
                });
            } else {
                setCheckedRows(prev => {
                    const newCheckedRows = { ...prev };
                    indices.forEach(index => {
                        newCheckedRows[index] = rosterData[index];
                    });
                    return newCheckedRows;
                });
            }
        };

        const isCurrentOrNextWeek = (weekEnding) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const currentWeekEnding = new Date(today);
            const daysUntilSunday = 7 - today.getDay();
            currentWeekEnding.setDate(today.getDate() + daysUntilSunday);

            const nextWeekEnding = new Date(currentWeekEnding);
            nextWeekEnding.setDate(currentWeekEnding.getDate() + 7);

            const targetWeekEnding = new Date(weekEnding);
            targetWeekEnding.setHours(0, 0, 0, 0);

            return targetWeekEnding.getTime() === currentWeekEnding.getTime() ||
                targetWeekEnding.getTime() === nextWeekEnding.getTime();
        };

        const onDuplicateWeekClick = (weekEnding) => {
            handleSelectWeek(weekEnding);

            const weekStartDate = processedData.weekStartDates[weekEnding];
            setSelectedWeek({
                startDate: weekStartDate,
                endDate: new Date(weekEnding)
            });
            setShowDupWeekModal(true);
        };

        const handleCheckboxChange = (originalIndex) => {
            setCheckedRows((prevState) => {
                const updatedRows = { ...prevState };
                if (updatedRows[originalIndex]) {
                    delete updatedRows[originalIndex];
                } else {
                    updatedRows[originalIndex] = rosterData[originalIndex];
                }

                const allChecked = Object.keys(updatedRows).length === rosterData.length;
                setSelectAll(allChecked);

                for (const [weekEnding, indices] of Object.entries(processedData.weekRowIndices)) {
                    const weekAllSelected = indices.length > 0 && indices.every(index => !!updatedRows[index]);
                    setWeekSelectStatus(prev => ({
                        ...prev,
                        [weekEnding]: weekAllSelected
                    }));
                }

                return updatedRows;
            });
        };

        const checkboxRenderer = ({ rowIndex }) => {
            if (processedData.subheaderIndices.has(rowIndex) ||
                processedData.headerRowIndices.has(rowIndex) ||
                processedData.addShiftIndices.has(rowIndex)) {
                return null;
            }

            const originalIndex = processedData.flatToOriginalMap[rowIndex];

            return (
                <div className="roster-checkbox">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        id={`checkbox-${originalIndex}`}
                        checked={!!checkedRows[originalIndex]}
                        onChange={() => handleCheckboxChange(originalIndex)}
                    />
                </div>
            );
        };

        useEffect(() => {
            if (duplicateFinancialWeek.weekCommencing) {
                const formatLocalDate = (dateString) => {
                    const date = new Date(dateString);
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                };

                const formattedStartDate = formatLocalDate(duplicateFinancialWeek.weekCommencing);
                setDuplicateDate(formattedStartDate);
            }
        }, [duplicateFinancialWeek]);

        const handleDateChange = (e) => {
            setDuplicateDate(e.target.value);
            setDuplicateFinancialWeek({});
        };

        useEffect(() => {
            const shouldDisableButton = Object.values(checkedRows).some(row => row.editable === false);
            setIsButtonDisabled(shouldDisableButton);
        }, [checkedRows]);

        const getRowHeight = ({ index }) => {
            if (processedData.headerRowIndices.has(index)) {
                return 30;
            } else if (processedData.addShiftIndices.has(index)) {
                return 40;
            }
            return processedData.subheaderIndices.has(index) ? 40 : 36;
        };
        
        const startTimeRenderer = ({ rowData }) => {
            if (!rowData.shiftStart) return null;

            if (rowData.isSubheader) return null;

            if (typeof rowData.shiftStart === 'string' && rowData.shiftStart.includes('T')) {
                return rowData.shiftStart.split('T')[1].substring(0, 5);
            }
            const date = new Date(rowData.shiftStart);
            return date.toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
        };

        const startDateRenderer = ({ rowData }) => {
            if (!rowData.shiftStart) return null;

            if (rowData.isSubheader) return null;

            const date = new Date(rowData.shiftStart);
            const dayOfWeek = date.toLocaleDateString('en-GB', { weekday: 'long' });
            const formattedDate = date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit'
            });

            return `${dayOfWeek} ${formattedDate}`;
        };
        
        const finishTimeRenderer = ({ rowData }) => {
            if (!rowData.shiftFinish) return null;

            if (rowData.isSubheader) return null;

            if (typeof rowData.shiftFinish === 'string' && rowData.shiftFinish.includes('T')) {
                return rowData.shiftFinish.split('T')[1].substring(0, 5);
            }
            const date = new Date(rowData.shiftFinish);
            return date.toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
        };
        
        const firstColumnCellRenderer = ({ rowIndex }) => {
            const rowData = processedData.flatData[rowIndex];
            if (!rowData) return null;

            if (processedData.subheaderIndices.has(rowIndex)) {
                const subheaderData = rowData;
                const weekEnding = subheaderData.weekEnding;
                const isWeekSelected = weekSelectStatus[weekEnding];

                return (
                    <div className="subheader-cell">
                        <div className="subheader-left">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={isWeekSelected}
                                    onChange={() => handleSelectWeek(weekEnding)}
                                />
                            </div>
                            Week {subheaderData.displayDateRange}
                            <button
                                className="duplicate-week-btn"
                                onClick={() => onDuplicateWeekClick(weekEnding)}
                            >
                                Duplicate Week
                            </button>
                        </div>
                        <div className="subheader-totals">
                            {subheaderData.totalHours} Hours × {subheaderData.totalShifts} Shifts × £{subheaderData.totalCost}
                        </div>
                    </div>
                );
            }

            return checkboxRenderer({ rowIndex });
        };

        const headerRowRenderer = ({ rowIndex, columnKey }) => {
            if (processedData.headerRowIndices.has(rowIndex)) {
                switch (columnKey) {
                    case 'weekEnding': return <div>Wk Ending</div>;
                    case 'StartDate': return <div>Start Date</div>;
                    case 'shiftStart': return <div>Start</div>;
                    case 'shiftFinish': return <div>Finish</div>;
                    case 'roleName': return <div>Role</div>;
                    case 'qty': return <div>Qty</div>;
                    case 'staffName': return <div>Staff Name</div>;
                    case 'notes': return <div>Notes</div>;
                    case 'billRate': return <div>Rate</div>;
                    default: return null;
                }
            }
            return null;
        };

        const dateRenderer = ({ cellData }) => {
            const date = new Date(cellData);
            const formattedDate = date.toLocaleDateString('en-GB');
            return <span>{formattedDate}</span>;
        };

        const editableRenderer = ({ rowData }) => {
            if (rowData.isPending) {
                const isCompleted = completedPendingIds && completedPendingIds.has(rowData.pendingId);
                if (isCompleted) {
                    return <FaCheck className="status-icon-check" />;
                }
                return <FaHourglassHalf className="status-icon-pending" />;
            }
            return (
                <>
                    {!rowData.editable && <FaLock className="lock-icon-blue" />}
                </>
            );
        };

        const staffNameRenderer = ({ cellData, rowData }) => {
            if (rowData.isPending) {
                const isCompleted = completedPendingIds && completedPendingIds.has(rowData.pendingId);
                return (
                    <div className={isCompleted ? 'staff-completed' : 'staff-pending'}>
                        <span>{isCompleted ? 'CREATED' : 'PENDING'}</span>
                    </div>
                );
            }

            if (cellData === 'UNCONFIRMED') {
                return (
                    <div className="staff-unconfirmed">
                        {cellData}
                    </div>
                );
            }

            return <div>{cellData}</div>;
        };

        const rateRenderer = ({ rowData }) => {
            if (rowData.isPending) {
                const isCompleted = completedPendingIds && completedPendingIds.has(rowData.pendingId);
                return <p>{isCompleted ? 'Created' : 'Pending'}</p>;
            }
            return (
                <p>&pound;{rowData.billRate.toFixed(2)}</p>
            );
        };

        const AddShiftRow = ({ rowData, weekDate, columnKey }) => {
            const [roles, setRoles] = useState([]);
            const [date, setDate] = useState('');
            const [startTime, setStartTime] = useState('00:00');
            const [endTime, setEndTime] = useState('00:00');
            const [notes, setNotes] = useState('');
            const [qty, setQty] = useState(1);
            const [selectedRole, setSelectedRole] = useState('DS');
            const [, setValidated] = useState(false);
            const [datesInWeek, setDatesInWeek] = useState([]);

            const getDatesInWeek = (startDate) => {
                const dates = [];
                const currentDate = new Date(startDate);
                currentDate.setHours(0, 0, 0, 0);

                for (let i = 0; i < 7; i++) {
                    dates.push(new Date(currentDate));
                    currentDate.setDate(currentDate.getDate() + 1);
                }
                return dates;
            };

            const isDateRestricted = (targetDate) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const checkDate = new Date(targetDate);
                checkDate.setHours(0, 0, 0, 0);

                if (checkDate < today) {
                    return true;
                }

                const twoWeeksFromToday = new Date(today);
                twoWeeksFromToday.setDate(today.getDate() + 14);

                if (futureDateRestrictProp === true) {
                    return checkDate <= twoWeeksFromToday;
                } else {
                    return false;
                }
            };

            const findMatchingWeekEnding = (targetDate) => {
                if (!rosterData || rosterData.length === 0) return null;

                const targetDateStr = formatDateValue(targetDate);

                for (const shift of rosterData) {
                    if (!shift.weekEnding) continue;

                    const existingDate = new Date(shift.weekEnding);
                    existingDate.setHours(0, 0, 0, 0);
                    const existingDateStr = formatDateValue(existingDate);

                    if (existingDateStr === targetDateStr) {
                        return shift.weekEnding;
                    }
                }

                return null;
            };

            useEffect(() => {
                if (weekDate) {
                    const weekStartDate = new Date(weekDate);
                    weekStartDate.setHours(0, 0, 0, 0);
                    const dates = getDatesInWeek(weekStartDate);
                    setDatesInWeek(dates);

                    let validDate = null;
                    for (const dateOption of dates) {
                        if (!isDateRestricted(dateOption)) {
                            validDate = formatDateValue(dateOption);
                            break;
                        }
                    }

                    if (validDate) {
                        setDate(validDate);
                    } else {
                        setDate('');
                        SHIFT_VALUES.date = '';
                    }
                }
            }, [weekDate, futureDateRestrictProp]);

            useEffect(() => {
                const uniqueRoles = Array.from(new Set(rolesFromParent.map(item => item.Role)));
                setRoles(uniqueRoles);
            }, [rolesFromParent.length > 0]);

            if (weekDate && datesInWeek.length > 0) {
                const allDatesRestricted = datesInWeek.every(dateOption => isDateRestricted(dateOption));
                if (allDatesRestricted) {
                    return null;
                }
            }

            const handleRoleChange = (roleItem) => {
                setSelectedRole(roleItem);
                SHIFT_VALUES.role = roleItem;
            };

            const handleSubmit = async (event) => {
                if (event && event.preventDefault) {
                    event.preventDefault();
                    event.stopPropagation();
                }

                if (!SHIFT_VALUES.startTime || !SHIFT_VALUES.endTime) {
                    console.error("Missing required fields in global state");
                    return;
                }

                setValidated(true);

                let targetDateString;
                if (date && SHIFT_VALUES.date) {
                    targetDateString = SHIFT_VALUES.date;
                } else {
                    targetDateString = date;
                }

                const [year, month, day] = targetDateString.split('-').map(Number);

                const startTimeParts = SHIFT_VALUES.startTime.split(':').map(Number);
                const endTimeParts = SHIFT_VALUES.endTime.split(':').map(Number);

                const startHours = startTimeParts[0] || 0;
                const startMinutes = startTimeParts[1] || 0;
                const endHours = endTimeParts[0] || 0;
                const endMinutes = endTimeParts[1] || 0;

                const pad = (n) => String(n).padStart(2, '0');
                const dateStr = `${year}-${pad(month)}-${pad(day)}`;
                const shiftStartStr = `${dateStr}T${pad(startHours)}:${pad(startMinutes)}:00`;
                const shiftFinishStr = `${dateStr}T${pad(endHours)}:${pad(endMinutes)}:00`;

                const shiftData = {
                    shiftstart: shiftStartStr,
                    shiftfinish: shiftFinishStr,
                    rolename: SHIFT_VALUES.role,
                    qty: SHIFT_VALUES.qty,
                    notes: SHIFT_VALUES.notes,
                    venueid: createShiftVals.venueid.toString(),
                    CODB: createShiftVals.codb,
                    bun: createShiftVals.bun.toString()
                };

                const startMins = startHours * 60 + startMinutes;
                let endMins = endHours * 60 + endMinutes;
                if (endMins <= startMins) endMins += 24 * 60;
                const duration = (endMins - startMins) / 60;

                const weekEnding = new Date(year, month - 1, day, 12, 0, 0);
                const dayOfWeek = weekEnding.getDay();
                const daysToAdd = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
                weekEnding.setDate(weekEnding.getDate() + daysToAdd);

                const existingWeekEnding = findMatchingWeekEnding(weekEnding);

                const pendingShifts = [];
                const baseTimestamp = Date.now();

                for (let i = 0; i < SHIFT_VALUES.qty; i++) {
                    const pendingId = `pending-${baseTimestamp}-${i}-${Math.random().toString(36).substr(2, 9)}`;

                    const pendingShift = {
                        pendingId: pendingId,
                        shiftId: pendingId,
                        shiftStart: shiftStartStr,
                        shiftFinish: shiftFinishStr,
                        roleName: SHIFT_VALUES.role,
                        staffName: 'PENDING',
                        notes: SHIFT_VALUES.notes,
                        weekEnding: existingWeekEnding || weekEnding.toISOString(),
                        editable: true,
                        billRate: 0,
                        totalHours: duration,
                        totalCost: 0,
                        codb: createShiftVals.codb,
                        site_name: selectedSite,
                        isPending: true
                    };

                    pendingShifts.push(pendingShift);
                }

                setPendingShiftsParent(prev => [...prev, ...pendingShifts]);
                setRosterData(prevShifts => [...prevShifts, ...pendingShifts]);

                setStartTime('00:00');
                setEndTime('00:00');
                setNotes('');
                setQty(1);
                SHIFT_VALUES.startTime = '00:00';
                SHIFT_VALUES.endTime = '00:00';
                SHIFT_VALUES.notes = '';
                SHIFT_VALUES.qty = 1;

                onShiftCreate(shiftData, pendingShifts.map(p => p.pendingId));
            };

            switch (columnKey) {
                case 'StartDate':
                    return <div className="add-shift-cell">
                        <Form.Select
                            value={date}
                            onChange={(e) => {
                                const newDate = e.target.value;
                                if (!isDateRestricted(new Date(newDate))) {
                                    setDate(newDate);
                                    SHIFT_VALUES.date = newDate;
                                }
                            }}
                            className="add-shift-select"
                            required
                        >
                            {datesInWeek.map((dateOption, index) => {
                                const dateValue = formatDateValue(dateOption);
                                const displayText = dateOption.toLocaleDateString('en-GB', {
                                    weekday: 'short',
                                    day: '2-digit',
                                    month: '2-digit'
                                });

                                const isRestricted = isDateRestricted(dateOption);

                                return (
                                    <option
                                        key={index}
                                        value={dateValue}
                                        disabled={isRestricted}
                                        className={isRestricted ? 'date-option-restricted' : ''}
                                    >
                                        {displayText}
                                    </option>
                                );
                            })}
                        </Form.Select>
                    </div>;

                case 'shiftStart':
                    return (
                        <div className="add-shift-cell">
                            <TimeInput
                                value={startTime}
                                onChange={(newTime) => {
                                    setStartTime(newTime);
                                    SHIFT_VALUES.startTime = newTime;
                                }}
                                id="shift-start-time"
                                className="add-shift-input"
                            />
                        </div>
                    );

                case 'shiftFinish':
                    return (
                        <div className="add-shift-cell">
                            <TimeInput
                                value={endTime}
                                onChange={(newTime) => {
                                    setEndTime(newTime);
                                    SHIFT_VALUES.endTime = newTime;
                                }}
                                id="shift-end-time"
                                className="add-shift-input"
                            />
                        </div>
                    );

                case 'roleName':
                    return (
                        <div className="add-shift-cell">
                            <Form.Select
                                value={selectedRole}
                                onChange={(e) => {
                                    handleRoleChange(e.target.value)
                                }}
                                className="add-shift-select"
                                required
                            >
                                {roles.map((roleItem, index) => {
                                    return (
                                        <option key={index} value={roleItem}>
                                            {roleItem}
                                        </option>
                                    );
                                })}
                            </Form.Select>
                        </div>
                    );

                case 'qty':
                    return (
                        <div className="add-shift-cell">
                            <Form.Control
                                required
                                type="number"
                                className="add-shift-input add-shift-qty me-3"
                                placeholder="Number"
                                id="shift-number"
                                value={qty === 0 ? '' : qty}
                                onChange={(e) => {
                                    const newQty = Math.abs(e.target.value);
                                    setQty(newQty);
                                    SHIFT_VALUES.qty = newQty;
                                }}
                            />
                        </div>
                    );

                case 'notes':
                    return (
                        <div className="add-shift-cell d-flex">
                            <div className="flex-grow-1">
                                <Form.Control
                                    className="add-shift-input"
                                    type="text"
                                    id="shift-notes"
                                    placeholder="Shift Notes"
                                    value={notes}
                                    onChange={(e) => {
                                        const newNotes = e.target.value;
                                        setNotes(newNotes);
                                        SHIFT_VALUES.notes = newNotes;
                                    }}
                                />
                            </div>
                            <div className="add-shift-btn-wrapper">
                                <Button variant="primary" className="create-shifts-btn w-100" onClick={handleSubmit}>
                                    Create Shifts
                                </Button>
                            </div>
                            <p className="add-shift-asterisk">* Member of staff will be allocated by security provider</p>
                        </div>
                    );

                case 'weekEnding':
                    return <div className="add-shift-cell"><span className="add-shift-label">Wk Ending</span></div>;

                default:
                    return null;
            }
        };

        return (
            <>
                <LoadingSpinner loading={loading} />
                <Modal show={showDupModal} onHide={handleCloseDupModal} className="text-center">
                    <Modal.Header closeButton>
                        <Modal.Title>Select start date of new pattern</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group>
                            <Form.Control
                                type="date"
                                id="duplicate-shifts"
                                value={duplicateDate}
                                onChange={handleDateChange}
                            />
                        </Form.Group>
                        <Dropdown className="ms-md-3">
                            <Dropdown.Toggle className="w-100 mb-3 mb-md-0 text-dark" variant="default" id="dropdown-basic">
                                {duplicateFinancialWeek.description || 'Financial Week'}
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="modal-dropdown-menu">
                                {financialWeeks.map((fwItem, index) => (
                                    <Dropdown.Item
                                        key={index}
                                        style={{
                                            opacity: futureDateRestrictProp && isCurrentOrNextWeek(fwItem.weekEnding) ? 0.5 : 1
                                        }}
                                        disabled={futureDateRestrictProp && isCurrentOrNextWeek(fwItem.weekEnding)}
                                        onClick={() => { setDuplicateFinancialWeek(fwItem) }}
                                    >{fwItem.description}</Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseDupModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={async () => { await onDuplicate(duplicateDate, checkedRows, handleCloseDupModal); }} >
                            Duplicate
                        </Button>
                    </Modal.Footer>
                </Modal>
                <Modal show={showDupWeekModal} onHide={handleCloseDupWeekModal} className="text-center">
                    <Modal.Header closeButton>
                        <Modal.Title>Select start date of new pattern.</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group>
                            <Form.Control
                                type="date"
                                id="duplicate-shifts"
                                value={duplicateDate}
                                onChange={handleDateChange}
                                hidden={true}
                            />
                        </Form.Group>
                        <Dropdown className="ms-md-3">
                            <Dropdown.Toggle className="w-100 mb-3 mb-md-0 text-dark" variant="default">
                                {duplicateFinancialWeek.description || 'Financial Week'}
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="modal-dropdown-menu">
                                {financialWeeks.map((fwItem, index) => (
                                    <Dropdown.Item
                                        key={index}
                                        style={{
                                            opacity: futureDateRestrictProp && isCurrentOrNextWeek(fwItem.weekEnding) ? 0.5 : 1
                                        }}
                                        disabled={futureDateRestrictProp && isCurrentOrNextWeek(fwItem.weekEnding)}
                                        onClick={() => { setDuplicateFinancialWeek(fwItem) }}
                                    >{fwItem.description}</Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseDupWeekModal}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => {
                                if (selectedWeek && duplicateDate) {
                                    onDuplicateWeek(selectedWeek.startDate, duplicateDate);
                                    handleCloseDupWeekModal();
                                } else {
                                    onDuplicate(duplicateDate, checkedRows, handleCloseDupWeekModal);
                                }
                            }}
                        >
                            Duplicate Week
                        </Button>
                    </Modal.Footer>
                </Modal>
                <Modal show={showRemoveModal} onHide={handleCloseRemoveModal} className="text-center">
                    <Modal.Header closeButton>
                        <Modal.Title>Are you sure you would like to delete selected shifts?</Modal.Title>
                    </Modal.Header>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseRemoveModal}>
                            Cancel
                        </Button>
                        <Button variant="primary danger" onClick={async () => { await onRemove(checkedRows, handleCloseRemoveModal) }}>
                            Confirm
                        </Button>
                    </Modal.Footer>
                </Modal>
                {noRosterDataMsg ? <div className="table-message">{noRosterDataMsg}</div> : (
                    <>
                        {Object.keys(checkedRows).length > 0 && <TableOptions />}
                        <AutoSizer disableHeight>
                            {({ width }) => (
                                <VirtualizedTable
                                    width={width}
                                    height={700}
                                    scrollingResetTimeInterval={0}
                                    headerHeight={0}
                                    rowRender={50}
                                    rowHeight={getRowHeight}
                                    rowCount={processedData.flatData.length}
                                    rowGetter={({ index }) => processedData.flatData[index]}
                                    rowClassName={({ index }) => {
                                        const rowData = processedData.flatData[index];
                                        if (!rowData) {
                                            return "mangerTableRow";
                                        }
                                        if (processedData.subheaderIndices.has(index)) {
                                            return "subheader-row";
                                        } else if (processedData.headerRowIndices.has(index)) {
                                            return "weekly-header-row";
                                        }
                                        const dataIdx = processedData.dataRowIndexMap[index];
                                        const parity = dataIdx !== undefined && dataIdx % 2 === 0 ? 'row-even' : 'row-odd';
                                        if (processedData.addShiftIndices.has(index)) {
                                            return `add-shift-row ${parity}`;
                                        }
                                        return `mangerTableRow ${parity}`;
                                    }}
                                >
                                    <Column
                                        maxWidth={45}
                                        minWidth={45}
                                        width={45}
                                        dataKey="checkbox"
                                        className="weekly-header-rowColumn"
                                        cellRenderer={firstColumnCellRenderer}
                                    />
                                    <Column
                                        maxWidth={45}
                                        minWidth={45}
                                        width={45}
                                        dataKey="editable"
                                        className="weekly-header-rowColumn"
                                        cellRenderer={({ rowIndex, rowData }) => {
                                            if (processedData.headerRowIndices.has(rowIndex)) {
                                                return null;
                                            } else if (processedData.subheaderIndices.has(rowIndex) ||
                                                processedData.addShiftIndices.has(rowIndex)) {
                                                return null;
                                            }
                                            return editableRenderer({ rowData });
                                        }}
                                    />
                                    <Column
                                        maxWidth={100}
                                        minWidth={100}
                                        width={100}
                                        className="weekly-header-rowColumn"
                                        dataKey="weekEnding"
                                        cellRenderer={({ rowIndex, rowData, cellData }) => {
                                            if (processedData.headerRowIndices.has(rowIndex)) {
                                                return headerRowRenderer({ rowIndex, columnKey: 'weekEnding' });
                                            } else if (processedData.addShiftIndices.has(rowIndex)) {
                                                return <AddShiftRow rowData={rowData} weekDate={rowData.weekStartDate} columnKey={'weekEnding'}/>
                                            }
                                            return dateRenderer({ cellData });
                                        }}
                                    />
                                    <Column
                                        maxWidth={180}
                                        minWidth={180}
                                        width={180}
                                        dataKey="shiftStart"
                                        className="weekly-header-rowColumn"
                                        cellRenderer={({ rowIndex, rowData }) => {
                                            if (processedData.headerRowIndices.has(rowIndex)) {
                                                return headerRowRenderer({ rowIndex, columnKey: 'StartDate' });
                                            } else if (processedData.subheaderIndices.has(rowIndex) ||
                                                processedData.addShiftIndices.has(rowIndex)) {
                                                return <AddShiftRow rowData={rowData} weekDate={rowData.weekStartDate} columnKey={'StartDate'}/>
                                            }
                                            return startDateRenderer({ rowIndex, rowData });
                                        }}
                                    />
                                    <Column
                                        maxWidth={90}
                                        minWidth={90}
                                        width={90}
                                        dataKey="shiftStart"
                                        className="weekly-header-rowColumn"
                                        cellRenderer={({ rowIndex, rowData }) => {
                                            if (processedData.headerRowIndices.has(rowIndex)) {
                                                return headerRowRenderer({ rowIndex, columnKey: 'shiftStart' });
                                            } else if (processedData.subheaderIndices.has(rowIndex) ||
                                                processedData.addShiftIndices.has(rowIndex)) {
                                                return <AddShiftRow rowData={rowData} weekDate={rowData.weekStartDate} columnKey={'shiftStart'}/>
                                            }
                                            return startTimeRenderer({ rowIndex, rowData });
                                        }}
                                    />
                                    <Column
                                        maxWidth={90}
                                        minWidth={90}
                                        width={90}
                                        dataKey="shiftFinish"
                                        className="weekly-header-rowColumn"
                                        cellRenderer={({ rowIndex, rowData }) => {
                                            if (processedData.headerRowIndices.has(rowIndex)) {
                                                return headerRowRenderer({ rowIndex, columnKey: 'shiftFinish' });
                                            } else if (processedData.subheaderIndices.has(rowIndex) ||
                                                processedData.addShiftIndices.has(rowIndex)) {
                                                return <AddShiftRow rowData={rowData} weekDate={rowData.weekStartDate} columnKey={'shiftFinish'}/>
                                            }
                                            return finishTimeRenderer({ rowIndex, rowData });
                                        }}
                                    />
                                    <Column
                                        maxWidth={130}
                                        minWidth={130}
                                        width={130}
                                        dataKey="roleName"
                                        className="weekly-header-rowColumn"
                                                                                cellRenderer={({ rowIndex, rowData }) => {
                                            if (processedData.headerRowIndices.has(rowIndex)) {
                                                return headerRowRenderer({ rowIndex, columnKey: 'roleName' });
                                            } else if (processedData.subheaderIndices.has(rowIndex) ||
                                                processedData.addShiftIndices.has(rowIndex)) {
                                                return <AddShiftRow rowData={rowData} weekDate={rowData.weekStartDate} columnKey={'roleName'}/>
                                            }
                                            return rowData.roleName;
                                        }}
                                    />
                                    <Column
                                        minWidth={100}
                                        width={100}
                                        maxWidth={100}
                                        dataKey="qty"
                                        className="weekly-header-rowColumn"
                                        cellRenderer={({ rowIndex, rowData }) => {
                                            if (processedData.headerRowIndices.has(rowIndex)) {
                                                return headerRowRenderer({ rowIndex, columnKey: 'qty' });
                                            } else if (processedData.subheaderIndices.has(rowIndex) ||
                                                processedData.addShiftIndices.has(rowIndex)) {
                                                return <AddShiftRow rowData={rowData} weekDate={rowData.weekStartDate} columnKey={'qty'}/>
                                            }
                                            return null;
                                        }}
                                    />
                                    <Column
                                        minWidth={270}
                                        width={270}
                                        dataKey="staffName"
                                        className="weekly-header-rowColumn"
                                                                                cellRenderer={({ rowIndex, rowData, cellData }) => {
                                            if (processedData.headerRowIndices.has(rowIndex)) {
                                                return headerRowRenderer({ rowIndex, columnKey: 'staffName' });
                                            } else if (processedData.subheaderIndices.has(rowIndex) ||
                                                processedData.addShiftIndices.has(rowIndex)) {
                                                return <AddShiftRow rowData={rowData} weekDate={rowData.weekStartDate} columnKey={'staffName'}/>
                                            }
                                            return staffNameRenderer({ cellData, rowData });
                                        }}
                                    />
                                    {showSecurityCosting === true && (
                                        <Column
                                            flexGrow={1}
                                            maxWidth={70}
                                            minWidth={70}
                                            width={70}
                                            dataKey="billRate"
                                            className="weekly-header-rowColumn"
                                            cellRenderer={({ rowIndex, rowData }) => {
                                                if (processedData.headerRowIndices.has(rowIndex)) {
                                                    return headerRowRenderer({ rowIndex, columnKey: 'billRate' });
                                                } else if (processedData.subheaderIndices.has(rowIndex) ||
                                                    processedData.addShiftIndices.has(rowIndex)) {
                                                    return null;
                                                }
                                                return rateRenderer({ rowData });
                                            }}
                                        />
                                    )}
                                    <Column
                                        minWidth={500}
                                        flexGrow={1}
                                        flexShrink={1}
                                        dataKey="notes"
                                        className="weekly-header-rowColumn"
                                                                                cellRenderer={({ rowIndex, rowData }) => {
                                            if (processedData.headerRowIndices.has(rowIndex)) {
                                                return headerRowRenderer({ rowIndex, columnKey: 'notes' });
                                            } else if (processedData.subheaderIndices.has(rowIndex) ||
                                                processedData.addShiftIndices.has(rowIndex)) {
                                                return <AddShiftRow rowData={rowData} weekDate={rowData.weekStartDate} columnKey={'notes'}/>
                                            }
                                            return rowData.notes;
                                        }}
                                    />
                                </VirtualizedTable>
                            )}
                        </AutoSizer>
                    </>
                )}
            </>
        );
    }

export default RosterDesktopTable;
